import { supabase } from '../lib/supabase'

// プロジェクト共有リンク（アカウント式リンク共有）のサービス層
// 裏側は既存のチーム基盤（teams / team_members / team_invitations）を利用する。
// 詳細: supabase/migrations/20260818100000_add_project_share_links.sql

export interface ShareLink {
  token: string
  role: 'editor' | 'viewer'
  expiresAt: string
}

export interface ShareLinkInfo {
  projectId: string
  projectName: string
  ownerName: string
  role: 'editor' | 'viewer'
  isValid: boolean
}

/** 共有リンクのURL文字列を生成 */
export function buildShareUrl(token: string): string {
  return `${window.location.origin}/share/${token}`
}

/** 共有リンクを発行（既に同ロールの有効リンクがあればそれを返す） */
export async function createShareLink(
  projectId: string,
  role: 'editor' | 'viewer'
): Promise<ShareLink> {
  const { data, error } = await supabase.rpc('create_project_share_link', {
    p_project_id: projectId,
    p_role: role
  })
  if (error) throw error
  const row = Array.isArray(data) ? data[0] : data
  if (!row) throw new Error('リンクの発行に失敗しました')
  return { token: row.link_token, role: row.link_role, expiresAt: row.link_expires_at }
}

/** プロジェクトの有効な共有リンク一覧を取得（オーナーのみ・SECURITY DEFINER RPC経由）
 *  注意: team_invitations の直接SELECTは禁止（トークン列挙防止のためRLSで封鎖済み） */
export async function getActiveShareLinks(projectId: string): Promise<ShareLink[]> {
  const { data, error } = await supabase.rpc('get_project_share_links', {
    p_project_id: projectId
  })
  if (error) throw error
  return (data || []).map((d: any) => ({
    token: d.link_token,
    role: d.link_role,
    expiresAt: d.link_expires_at
  }))
}

/** 共有リンクの情報を取得（未ログインでも可。参加確認画面用） */
export async function getShareLinkInfo(token: string): Promise<ShareLinkInfo | null> {
  const { data, error } = await supabase.rpc('get_project_share_link_info', {
    p_token: token
  })
  if (error) throw error
  const row = Array.isArray(data) ? data[0] : data
  if (!row || !row.is_valid) return null
  return {
    projectId: row.project_id,
    projectName: row.project_name,
    ownerName: row.owner_name || '',
    role: row.member_role,
    isValid: true
  }
}

/** 共有リンクで参加（要ログイン） */
export async function acceptShareLink(
  token: string
): Promise<{ projectId: string; projectName: string; alreadyMember: boolean }> {
  const { data, error } = await supabase.rpc('accept_project_share_link', {
    p_token: token
  })
  if (error) throw error
  const row = Array.isArray(data) ? data[0] : data
  if (!row) throw new Error('参加に失敗しました')
  return {
    projectId: row.project_id,
    projectName: row.project_name,
    alreadyMember: row.already_member
  }
}

/** 共有リンクをすべて無効化（オーナーのみ。参加済みメンバーは残る） */
export async function revokeShareLinks(projectId: string): Promise<number> {
  const { data, error } = await supabase.rpc('revoke_project_share_links', {
    p_project_id: projectId
  })
  if (error) throw error
  return data ?? 0
}

/** 共有メンバーを削除（オーナーのみ） */
export async function removeProjectMember(projectId: string, memberUserId: string): Promise<void> {
  const { error } = await supabase.rpc('remove_project_member', {
    p_project_id: projectId,
    p_member_user_id: memberUserId
  })
  if (error) throw error
}
